import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { Eye, EyeOff, Loader2, Check, ChevronsUpDown, MapPin, X, Gift } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { contentApi } from "../lib/api";
import { addBreadcrumb, captureError } from "../lib/sentry";

const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Argentina","Armenia","Australia",
  "Austria","Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium",
  "Belize","Benin","Bhutan","Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei",
  "Bulgaria","Burkina Faso","Burundi","Cambodia","Cameroon","Canada","Central African Republic",
  "Chad","Chile","China","Colombia","Comoros","Congo","Costa Rica","Croatia","Cuba","Cyprus",
  "Czech Republic","Denmark","Djibouti","Dominican Republic","Ecuador","Egypt","El Salvador",
  "Estonia","Ethiopia","Fiji","Finland","France","Gabon","Gambia","Georgia","Germany","Ghana",
  "Greece","Guatemala","Guinea","Haiti","Honduras","Hungary","Iceland","India","Indonesia",
  "Iran","Iraq","Ireland","Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya",
  "Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Libya","Liechtenstein","Lithuania",
  "Luxembourg","Madagascar","Malaysia","Maldives","Mali","Malta","Mexico","Moldova","Monaco",
  "Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia","Nepal","Netherlands",
  "New Zealand","Nicaragua","Niger","Nigeria","North Macedonia","Norway","Oman","Pakistan",
  "Panama","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania","Russia",
  "Rwanda","Saudi Arabia","Senegal","Serbia","Singapore","Slovakia","Slovenia","Somalia",
  "South Africa","South Korea","Spain","Sri Lanka","Sudan","Sweden","Switzerland","Syria",
  "Taiwan","Tanzania","Thailand","Togo","Trinidad and Tobago","Tunisia","Turkey","Uganda",
  "Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan",
  "Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
];

const CURRENCIES = [
  { code: "usd", label: "USD ($)", symbol: "$" },
  { code: "eur", label: "EUR (\u20ac)", symbol: "\u20ac" },
  { code: "gbp", label: "GBP (\u00a3)", symbol: "\u00a3" },
];

const CountryCombobox = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = search
    ? COUNTRIES.filter(c => c.toLowerCase().includes(search.toLowerCase()))
    : COUNTRIES;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full h-12 text-base justify-between font-normal"
          data-testid="signup-country-select"
        >
          {value || "Select your country"}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type to search..."
            value={search}
            onValueChange={setSearch}
            data-testid="country-search-input"
          />
          <CommandList className="max-h-60">
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {filtered.map((c) => (
                <CommandItem
                  key={c}
                  value={c}
                  onSelect={() => { onChange(c); setOpen(false); setSearch(""); }}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === c ? "opacity-100" : "opacity-0"}`} />
                  {c}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const HomeCityInput = ({ value, onChange }) => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await contentApi.searchAutocomplete(query, null, null, {
          types: "locality,administrative_area_level_3,postal_town",
        });
        const data = res.data || {};
        setSuggestions(data.suggestions || []);
        setOpen((data.suggestions || []).length > 0);
      } catch (error) {
        captureError(error, { action: "signup_home_city_autocomplete", queryLength: query.length });
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleSelect = async (suggestion) => {
    setOpen(false);
    setQuery(suggestion.description || suggestion.name);
    setSuggestions([]);
    try {
      const res = await contentApi.getPlaceDetails(suggestion.place_id);
      const data = res.data || {};
      if (data.latitude && data.longitude) {
        onChange({ name: suggestion.description || suggestion.name, lat: data.latitude, lng: data.longitude });
      }
    } catch (error) {
      captureError(error, { action: "signup_home_city_details" });
      onChange({ name: suggestion.description || suggestion.name, lat: null, lng: null });
    }
  };

  const handleClear = () => {
    setQuery("");
    setSuggestions([]);
    onChange(null);
  };

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Start typing your city..."
          value={value ? (query || value.name) : query}
          onChange={(e) => { setQuery(e.target.value); if (value) onChange(null); }}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          className="h-12 text-base pl-9 pr-9"
          data-testid="signup-homecity-input"
        />
        {(value || query) && (
          <button type="button" onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              type="button"
              className="w-full text-left px-3 py-2.5 text-sm hover:bg-accent transition-colors first:rounded-t-lg last:rounded-b-lg"
              onClick={() => handleSelect(s)}
              data-testid={`city-suggestion-${s.place_id}`}
            >
              {s.description}
            </button>
          ))}
        </div>
      )}
      {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
    </div>
  );
};

const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedRedirect = searchParams.get("redirect") || "/explore";
  const redirectTo = requestedRedirect.startsWith("/") && !requestedRedirect.startsWith("//")
    ? requestedRedirect
    : "/explore";
  const giftCode = searchParams.get("gift");
  const initialReferralCode = (searchParams.get("ref") || "").trim().toUpperCase();
  const { signup } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("");
  const [currency, setCurrency] = useState("usd");
  const [homeCity, setHomeCity] = useState(null);
  const [referralCode, setReferralCode] = useState(initialReferralCode);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    addBreadcrumb("Signup submitted", "auth.signup", {
      hasCountry: Boolean(country),
      hasHomeCity: Boolean(homeCity),
      currency,
      redirectTo,
      hasGiftCode: Boolean(giftCode),
    });
    try {
      const displayName = name || email.split("@")[0];
      const result = await signup(
        email,
        password,
        displayName,
        country,
        currency,
        homeCity,
        referralCode,
      );
      if (result.status === "verification_required") {
        addBreadcrumb("Signup requires email verification", "auth.signup", { redirectTo });
        if (result.code) {
          toast.info(`Your verification code is: ${result.code}`, { duration: 30000 });
        }
        localStorage.setItem("needs_location_onboarding", "true");
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
      }
    } catch (error) {
      if (!error.response || error.response.status >= 500) {
        captureError(error, { action: "signup", redirectTo, hasGiftCode: Boolean(giftCode) });
      }
      const message = error.response?.data?.detail || "Signup failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const benefits = giftCode
    ? [
        "Claim your free gifted audio guide",
        "Access to thousands of locations",
        "High-quality AI narration"
      ]
    : [
        "Your first audio guide is free",
        "Access to thousands of locations",
        "High-quality AI narration"
      ];

  return (
    <div className="min-h-screen bg-background">
      {/* Paper texture */}
      <div className="fixed inset-0 paper-texture pointer-events-none" />
      
      {/* Header */}
      <header className="p-4 relative z-10">
        <Link to="/" className="inline-flex items-center gap-2">
          <img 
            src="/icon-192.png" 
            alt="Culture Ken"
            className="w-10 h-10 rounded-xl"
          />
          <span className="text-xl font-bold text-foreground">
            Culture Ken
          </span>
        </Link>
      </header>

      {/* Main Content - naturally scrollable */}
      <main className="relative z-10 p-4 pb-12">
        <Card className="w-full max-w-md mx-auto shadow-lg">
          <CardHeader className="space-y-2 text-center">
            <CardTitle className="text-3xl font-bold">
              Create Account
            </CardTitle>
            <CardDescription className="text-base">
              Start exploring the world's most fascinating places
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Benefits */}
            <div className="mb-6 p-4 bg-muted/50 rounded-xl">
              <p className="text-sm font-medium text-foreground mb-3">What you'll get:</p>
              <ul className="space-y-2">
                {benefits.map((benefit, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-base">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 text-base"
                  data-testid="signup-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-base">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="At least 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 text-base pr-12"
                    data-testid="signup-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="referral-code" className="text-base">Referral code</Label>
                <div className="relative">
                  <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="referral-code"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    placeholder="Optional"
                    autoCapitalize="characters"
                    autoComplete="off"
                    maxLength={32}
                    className="h-12 text-base pl-11 uppercase"
                    data-testid="signup-referral-code-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  You get 5 credits when you sign up and I get 2 credits when you download your first audio guide!
                </p>
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 text-lg rounded-xl font-medium"
                disabled={loading}
                data-testid="signup-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  "Create Free Account"
                )}
              </Button>
            </form>
            
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-background px-2 text-muted-foreground">or</span></div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 text-base rounded-xl font-medium gap-3"
              onClick={() => {
                sessionStorage.setItem("post_auth_redirect", redirectTo);
                if (referralCode.trim()) {
                  sessionStorage.setItem("pending_referral_code", referralCode.trim().toUpperCase());
                } else {
                  sessionStorage.removeItem("pending_referral_code");
                }
                const redirect = `${window.location.origin}/auth/callback`;
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
              }}
              data-testid="google-signin-btn"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Sign in with Google
            </Button>

            <div className="mt-6 text-center">
              <p className="text-muted-foreground">
                Already have an account?{" "}
                <Link 
                  to={`/login${redirectTo !== "/explore" ? `?redirect=${encodeURIComponent(redirectTo)}` : ""}`}
                  className="text-primary font-medium hover:underline"
                  data-testid="goto-login-link"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SignupPage;
